import { PDFDocument } from '@cantoo/pdf-lib';
import { downloadFile, readableDate, Storage } from '@chocolate-soup-inc/cs-api-consumer-utils';
import type { TTableCellContext as CellContext, TTableRowSelection as RowSelectionState } from '@chocolate-soup-inc/cs-frontend-components';
import { ExtendedFAB, Icon, Link, Modal, styleClassNames, tableClassNames, TableInner, Tooltip } from '@chocolate-soup-inc/cs-frontend-components';
import clsx from 'clsx';
import _ from 'lodash';
import { quickScore } from 'quick-score';
import { createRef, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { serializeError } from 'serialize-error';
import { Filters, TFiltersProps } from '../../components/filters/Filters';
import { FloatingActions } from '../../components/floatingActions/FloatingActions';
import { TablePage, TTablePageRef } from '../../components/tablePage/TablePage';
import { useFilter } from '../../contexts/filters';
import { TShipmentType, useQueryAllShippedShipments } from '../../entities/shipments/queries';
import { getEventDateString, getShipDate, getShipmentAddressInfo, useShipmentAddressInfo } from '../../entities/shipments/shared';
import { TShipmentStatuses, useReshipShipmentMutation } from '../../generated/graphql';
import { TrackingDetailsModal } from './TrackingDetailsModal/TrackingDetailsModal';

export const RecipientCell = (context: CellContext<TShipmentType, unknown>) => {
  const { addressTypeIcon, recipientName, readableAddressType } = useShipmentAddressInfo(context.cell.row.original);

  return (
    <div className={clsx(styleClassNames.inlineLabel, tableClassNames.primary)}>
      {addressTypeIcon && readableAddressType && (
        <Tooltip autoPlacement={false} message={readableAddressType} placement='bottom'>
          <Icon filled={true} icon={addressTypeIcon} />
        </Tooltip>
      )}
      {recipientName}
    </div>
  );
};

export const ShipmentsTracking = () => {
  const { filtersPageMode } = useFilter();
  useEffect(() => {
    filtersPageMode();
  }, []); //eslint-disable-line

  const tableRef = createRef<TTablePageRef>();

  const [companyFilter, setCompanyFilter] = useState<string>();
  const [trackingNumberFilter, setTrackingNumberFilter] = useState<string>();
  const [statusFilter, setStatusFilter] = useState<string>();
  const [recipientFilter, setRecipientFilter] = useState<string>();

  const { data: shipments, error, loading } = useQueryAllShippedShipments();

  const [selectedRows, setSelectedRows] = useState<string[]>();

  const onRowSelectionChange = useCallback((rowSelection: RowSelectionState) => {
    console.log('selectedRows?:', rowSelection);
    setSelectedRows(Object.keys(rowSelection));
  }, []);

  const allCompanies = useMemo(() => {
    return _.uniqBy(_.compact(shipments.map((s) => s.company)), 'id');
  }, [shipments]);

  const companies = useMemo(() => {
    return _.uniq(_.compact(shipments.map((s) => allCompanies.find((c) => c.id === s.companyId))));
  }, [allCompanies, shipments]);

  const getRowId = useCallback((shipment: TShipmentType) => {
    return shipment.id;
  }, []);

  const filters = useMemo(() => {
    const filtersList: TFiltersProps['filters'] = [];

    filtersList.push({
      disabled: companies.length === 0,
      type: 'singleSelect',
      label: 'Company',
      name: 'companyId',
      onChange: (v) => {
        setCompanyFilter(v != null ? (v as string) : undefined);
      },
      options: companies?.map((c) => ({ label: c.name, value: c.id })),
      value: companyFilter
    });

    filtersList.push({
      type: 'textInput',
      label: 'Recipient',
      name: 'recipientName',
      onChange: (v) => {
        setRecipientFilter(v != null ? (v as string) : undefined);
      },
      value: recipientFilter
    });

    filtersList.push({
      type: 'textInput',
      label: 'Tracking Number',
      name: 'trackingNumber',
      onChange: (v) => {
        setTrackingNumberFilter(v != null ? (v as string) : undefined);
      },
      value: trackingNumberFilter
    });

    filtersList.push({
      type: 'singleSelect',
      label: 'Status',
      name: 'status',
      onChange: (v) => {
        setStatusFilter(v != null ? (v as TShipmentStatuses) : undefined);
      },
      options: [
        {
          label: 'In transit',
          value: TShipmentStatuses.Shipped
        },
        {
          label: 'Attention',
          value: TShipmentStatuses.Attention
        },
        {
          label: 'Delivered',
          value: TShipmentStatuses.Delivered
        },
        {
          label: 'Returned',
          value: TShipmentStatuses.Returned
        },
        {
          label: 'Error',
          value: TShipmentStatuses.Error
        }
      ],
      value: statusFilter
    });

    return filtersList;
  }, [companies, companyFilter, recipientFilter, statusFilter, trackingNumberFilter]);

  const filteredShipments = useMemo(() => {
    return shipments.filter((s) => {
      if (companyFilter != null && s.companyId !== companyFilter) return false;

      if (trackingNumberFilter != null && trackingNumberFilter !== '' && (s.trackingNumber == null || quickScore(s.trackingNumber, trackingNumberFilter) < 0.7)) return false;

      if (statusFilter != null && s.status !== statusFilter) return false;

      const { recipientName } = getShipmentAddressInfo({ shipment: s });
      if (recipientFilter && quickScore(recipientFilter, recipientName ?? '') < 0.7) return false;

      return true;
    });
  }, [companyFilter, recipientFilter, shipments, statusFilter, trackingNumberFilter]);

  //console.log('shipments OLD?:', shipments);
  console.log('filteredShipments OLD?:', filteredShipments);

  const [printAllLoading, setPrintAllLoading] = useState<boolean>(false);

  const onPrintAllClick = useCallback(async () => {
    setPrintAllLoading(true);

    try {
      const pdfDoc = await PDFDocument.create();
      for (const id of selectedRows || []) {
        const shipment = filteredShipments.find((s) => s.id === id);
        if (!shipment?.label?.labelFile) continue;

        const signedURL = await Storage.get(shipment.label.labelFile.key, {
          bucket: shipment.label.labelFile.bucket,
          region: shipment.label.labelFile.region
        });

        const arrayBuffer = await fetch(signedURL).then((res) => res.arrayBuffer());
        const doc = await PDFDocument.load(arrayBuffer);

        const copiedPages = await pdfDoc.copyPages(doc, doc.getPageIndices());
        copiedPages.forEach((p) => pdfDoc.addPage(p));
      }
      downloadFile({
        data: [await pdfDoc.save()] as BlobPart[],
        filetype: 'application/pdf',
        filename: 'print-labels.pdf'
      });
    } catch (error) {
      console.error(serializeError(error));
      toast.error('There was an error trying to print all labels.');
    } finally {
      setPrintAllLoading(false);
    }
  }, [selectedRows, filteredShipments]);

  const [reshipShipment] = useReshipShipmentMutation();
  const [reshipLoading, setReshipLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [recipientGifts, setRecipientGifts] = useState<any[] | null | undefined>(null);
  const [shipment, setShipment] = useState<TShipmentType | null | undefined>(null);

  const openModalDisplay = (original?: null | Record<string, any>) => {
    setShipment((original as TShipmentType) || {});
    setRecipientGifts(original?.gifts?.items || original?.gifts || []);
    setIsModalOpen(true);
  };

  const reshipShipments = useCallback(async () => {
    setReshipLoading(true);
    let recordId: string | undefined;
    try {
      for (const id of selectedRows || []) {
        const shipment = filteredShipments.find((item) => item.id === id);
        if (!shipment) continue;
        recordId = shipment.id;
        await reshipShipment({ variables: { id: shipment.id, companyId: shipment.companyId, version: shipment._version } });
      }
      toast.success('The shipments were successfully set to reship.');
      tableRef.current?.resetConfirmationModal();
      setSelectedRows(undefined);
    } catch (error) {
      console.error('Shipment reshipShipments failed', { shipmentId: recordId, error: serializeError(error) });
      toast.error('There was an error reshipping the shipments.');
    } finally {
      setReshipLoading(false);
    }
  }, [filteredShipments, reshipShipment, selectedRows, tableRef]);

  const onReshipShipmentsClick = useCallback(() => {
    tableRef.current?.setConfirmationProps({
      headline: `Reship ${selectedRows?.length} Shipments?`,
      supportingText: `Are you sure you want to reship these ${selectedRows?.length} shipments? By reshipping, they will be moved back to "Ready to Ship" and then it will be possible to print a new label for the same shipments.`,
      confirmLabel: 'Reship',
      onConfirmClick: reshipShipments,
      open: true
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reshipShipments, selectedRows, tableRef, tableRef.current]);

  return (
    <TablePage dataLoading={loading} actionsLoading={reshipLoading} error={error} ref={tableRef} title='Shipments Tracking'>
      <Filters filters={filters} />
      <div style={{ overflow: 'auto' }}>
        <TableInner<TShipmentType>
          enableSorting={true}
          defaultSorting={{ id: 'label_createdAt', desc: false }}
          className={styleClassNames.scrollingTable}
          data={filteredShipments}
          emptyText={shipments.length === 0 ? 'No shipments to be tracked.' : 'No shipments for the current filters'}
          expandable={false}
          fillSelectedRows={true}
          fixedHeader={true}
          forceExpanded={true}
          getRowId={getRowId}
          hideSubRowsSelect={true}
          hoverableRows={!reshipLoading}
          onRowSelectionChange={onRowSelectionChange}
          selectableRows={true}
          selectingChildSelectsFather={true}
          onRowClick={(e) => {
            openModalDisplay(e.original);
          }}
          columns={[
            {
              header: 'Company',
              accessorKey: 'company.name',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(tableClassNames.primary)}>
                    <span>{allCompanies.find((c) => c.id === cell.row.original.companyId)?.name}</span>
                  </div>
                );
              }
            },
            {
              header: 'Recipient',
              accessorKey: 'name',
              cell: RecipientCell
            },
            {
              header: 'Event Date',
              accessorKey: 'gifts.items.0.eventDate',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(tableClassNames.tertiary)}>
                    <span>{getEventDateString(cell.row.original)}</span>
                  </div>
                );
              }
            },
            {
              header: 'Ship Date',
              accessorKey: 'label.createdAt',
              cell: ({ cell }) => {
                const date = getShipDate(cell.row.original);
                return (
                  <div className={clsx(tableClassNames.tertiary)}>
                    <span>{readableDate(date, 'do MMM, yyyy')}</span>
                  </div>
                );
              }
            },
            {
              header: 'Tracking Number',
              accessorKey: 'trackingNumber',
              cell: ({ cell }) => (
                <div className={clsx(tableClassNames.primary)}>
                  {cell.row.original.trackingNumber && cell.row.original.trackingURL ? (
                    <Link
                      href={cell.row.original.trackingURL}
                      label={cell.row.original.trackingNumber}
                      icon={true}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        e.preventDefault();
                        window.open(cell.row.original.trackingURL as string, '_blank');
                      }}
                    />
                  ) : (
                    '-'
                  )}
                </div>
              )
            }
          ]}
        />
      </div>
      {selectedRows && selectedRows.length > 0 && (
        <FloatingActions>
          <ExtendedFAB
            // disabled={printAllLoading}
            label={`Print ${selectedRows.length} label${selectedRows.length > 1 ? 's' : ''}`}
            leadingIcon='print'
            loading={printAllLoading}
            onClick={onPrintAllClick}
            variant='tertiary'
          />
          <ExtendedFAB label={`Reship ${selectedRows.length} shipment${selectedRows.length > 1 ? 's' : ''}`} leadingIcon='replay' loading={reshipLoading} onClick={onReshipShipmentsClick} variant='surface' />
        </FloatingActions>
      )}
      <Modal
        headline='Tracking Details'
        open={isModalOpen}
        size='large'
        closeModal={() => {
          setIsModalOpen(false);
        }}>
        <TrackingDetailsModal shipment={shipment} recipientGifts={recipientGifts} />
      </Modal>
    </TablePage>
  );
};
